import { Banner, Category, ShippingRule, SeoConfig, FlashDealConfig, SocialProofConfig, EmailTemplate, EmailCampaign, MarketingConfig } from '../types';
import { marketingRepository } from '../../data/marketingRepository';

export const marketingService = {

  // --- BANNERS ---
  async getBanners(): Promise<Banner[]> {
    return marketingRepository.getBanners();
  },

  async saveBanner(bannerData: Partial<Banner>): Promise<{ success: boolean; message: string; data?: Banner }> {
    if (!bannerData.title || !bannerData.imageUrl) {
      return { success: false, message: 'Título e imagen son obligatorios' };
    }

    if (bannerData.imageUrl && !/^(http|https):\/\/[^ "]+$/.test(bannerData.imageUrl)) {
      return { success: false, message: 'URL de imagen inválida' };
    }

    const banner: Banner = {
      id: bannerData.id || `b-${Date.now()}`,
      title: bannerData.title,
      subtitle: bannerData.subtitle || '',
      imageUrl: bannerData.imageUrl,
      linkTo: bannerData.linkTo || '/catalog',
      zone: bannerData.zone || 'hero_slider',
      targetAudience: bannerData.targetAudience || 'all',
      isActive: bannerData.isActive !== undefined ? bannerData.isActive : true,
      order: bannerData.order || 1
    };

    await marketingRepository.saveBanner(banner);
    return { success: true, message: 'Banner guardado', data: banner };
  },

  async toggleBanner(bannerId: string, currentState: boolean): Promise<boolean> {
    const banners = await marketingRepository.getBanners();
    const banner = banners.find(b => b.id === bannerId);
    if (banner) {
      banner.isActive = !currentState;
      await marketingRepository.saveBanner(banner);
      return true;
    }
    return false;
  },

  async deleteBanner(id: string): Promise<boolean> {
    await marketingRepository.deleteBanner(id);
    return true;
  },

  async deleteAllBanners(): Promise<void> {
    await marketingRepository.deleteAllBanners();
  },

  // --- CATEGORIES ---
  async getCategories(): Promise<Category[]> {
    return marketingRepository.getCategories();
  },

  async saveCategory(catData: Partial<Category>): Promise<{ success: boolean; message: string; data?: Category }> {
    if (!catData.name || !catData.icon) {
      return { success: false, message: 'Nombre e icono son obligatorios' };
    }

    if (catData.imageUrl && !/^(http|https):\/\/[^ "]+$/.test(catData.imageUrl)) {
      return { success: false, message: 'URL de imagen inválida' };
    }

    const category: Category = {
      id: catData.id || `c-${Date.now()}`,
      name: catData.name,
      icon: catData.icon,
      color: catData.color || 'text-gray-600',
      imageUrl: catData.imageUrl,
      isActive: catData.isActive !== undefined ? catData.isActive : true,
      order: catData.order || 100,
      type: catData.type || 'all'
    };

    await marketingRepository.saveCategory(category);
    return { success: true, message: 'Categoría guardada', data: category };
  },

  async deleteCategory(id: string): Promise<{ success: boolean; message: string }> {
    await marketingRepository.deleteCategory(id);
    return { success: true, message: 'Categoría eliminada' };
  },

  async deleteAllCategories(): Promise<void> {
    await marketingRepository.deleteAllCategories();
  },

  // --- EMAIL TEMPLATES ---
  async getTemplates(): Promise<EmailTemplate[]> {
    return marketingRepository.getTemplates();
  },

  async saveTemplate(template: EmailTemplate): Promise<void> {
    await marketingRepository.saveTemplate(template);
  },

  // --- EMAIL CAMPAIGNS ---
  async getCampaigns(): Promise<EmailCampaign[]> {
    return marketingRepository.getCampaigns();
  },

  async saveCampaign(campaign: EmailCampaign): Promise<void> {
    await marketingRepository.saveCampaign(campaign);
  },

  // --- CONFIGURATION ---
  subscribeToConfig(callback: (config: MarketingConfig) => void, onError?: (error: Error) => void): () => void {
    return marketingRepository.subscribeToConfig(callback, onError);
  },

  async getConfig(): Promise<MarketingConfig | null> {
    const config = await marketingRepository.getConfig();
    return config as MarketingConfig | null;
  },

  async updateShippingRules(rule: Partial<ShippingRule>): Promise<void> {
    const config = await marketingRepository.getConfig();
    const newRule = { ...config.shippingRule, ...rule };
    await marketingRepository.updateConfig({ shippingRule: newRule });
  },

  async updateCosts(costs: Partial<{ local: number; national: number }>): Promise<void> {
    const config = await marketingRepository.getConfig();
    const newCosts = { ...config.shippingCosts, ...costs };
    await marketingRepository.updateConfig({ shippingCosts: newCosts });
  },

  async updateSEO(seo: Partial<SeoConfig>): Promise<void> {
    const config = await marketingRepository.getConfig();
    const newSeo = { ...config.seo, ...seo };
    await marketingRepository.updateConfig({ seo: newSeo });
  },

  async updateFlashDeals(flash: Partial<FlashDealConfig>): Promise<void> {
    const config = await marketingRepository.getConfig();
    const newFlash = { ...config.flashDeals, ...flash };
    await marketingRepository.updateConfig({ flashDeals: newFlash });
  },

  async updateSocialProof(proof: Partial<SocialProofConfig>): Promise<void> {
    const config = await marketingRepository.getConfig();
    const newProof = { ...config.socialProof, ...proof };
    await marketingRepository.updateConfig({ socialProof: newProof });
  },

  async updatePickupAddress(address: string): Promise<void> {
    await marketingRepository.updateConfig({ pickupAddress: address });
  },

  async updateConfig(updates: Record<string, unknown>): Promise<void> {
    await marketingRepository.updateConfig(updates);
  }
};
